"use client";

import { useEffect, useState } from "react";
import Image from "next/image";
import { api, type EventoEsternoDto } from "@/lib/api";
import { resolveMediaUrl } from "@/lib/media-url";
import { ScrollReveal } from "@/components/ScrollReveal";

interface EventiEsterniBlockProps {
  title: string;
  maxItems?: number;
  showArchiveLink?: boolean;
  archiveLinkUrl?: string;
  archiveLinkText?: string;
  className?: string;
}

/** Locandina dell'evento esterno (foto o volantino) come media-id → URL. */
function posterUrl(e: EventoEsternoDto): string | null {
  const id = e.fotoMediaId ?? e.volantinoMediaId;
  if (!id) return null;
  return resolveMediaUrl({ id } as unknown as Parameters<typeof resolveMediaUrl>[0]);
}

function formatData(iso: string | null): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("it-IT", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/** Card "evento segnalato": locandina GRANDE come le card dei nostri eventi.
 *  `featured` = layout orizzontale (poster a sinistra) usato quando c'è un solo
 *  evento, per riempire l'intera striscia senza un poster verticale gigante. */
function EsternoCard({ e, featured = false }: { e: EventoEsternoDto; featured?: boolean }) {
  const src = posterUrl(e);
  const d = e.data ? new Date(e.data) : null;
  const month = d ? d.toLocaleDateString("it-IT", { month: "short" }).toUpperCase() : "";
  const day = d ? d.getDate() : "";
  // urlEsterna (linkUrl) vince; altrimenti la nostra pagina di dettaglio.
  const href = e.linkUrl || `/eventi-segnalati/${e.slug}`;
  const extProps = e.linkUrl ? { target: "_blank", rel: "noopener noreferrer" } : {};

  const media = (
    <div className="relative overflow-hidden bg-neutral-50">
      {src ? (
        <Image
          src={src}
          alt={e.title}
          width={600}
          height={820}
          sizes="(min-width: 1024px) 300px, (min-width: 640px) 33vw, 50vw"
          className="w-full h-auto group-hover:scale-[1.02] transition-transform duration-500"
        />
      ) : (
        <div className="w-full aspect-[3/4] flex items-center justify-center text-neutral-300">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
      )}
      {d && (
        <div className="absolute top-2 right-2 bg-amber-600 text-white rounded-lg px-2 py-1 text-center leading-tight shadow-md">
          <div className="text-[9px] font-bold uppercase tracking-wide">{month}</div>
          <div className="text-lg font-bold leading-none">{day}</div>
        </div>
      )}
      <div className="absolute top-2 left-2 bg-amber-600 text-white text-[9px] font-bold uppercase tracking-wider px-2 py-0.5 rounded shadow-sm">
        Segnalato
      </div>
    </div>
  );

  if (featured) {
    // Singolo evento: card orizzontale a piena larghezza (poster a sinistra,
    // testo grande a destra) — riempie la striscia, niente vuoti.
    return (
      <article className="group bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100 flex flex-col sm:flex-row">
        <a href={href} {...extProps} className="block sm:w-64 lg:w-72 shrink-0">{media}</a>
        <div className="p-5 sm:p-7 flex flex-col justify-center">
          <h3 className="font-bold text-xl sm:text-2xl text-[#1a3a5c] leading-snug">
            <a href={href} {...extProps} className="hover:text-amber-700 transition-colors">{e.title}</a>
          </h3>
          <p className="mt-2 text-sm sm:text-base font-semibold text-neutral-700">
            {formatData(e.data)}
            {e.oraInizio && <> &middot; ore {e.oraInizio.slice(0, 5)}</>}
          </p>
          {e.location && <p className="mt-1 text-sm text-neutral-500">{e.location}</p>}
        </div>
      </article>
    );
  }

  return (
    <article className="group h-full bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 border border-neutral-100 flex flex-col">
      <a href={href} {...extProps} className="block">{media}</a>
      <div className="p-3 sm:p-4">
        <h3 className="font-bold text-sm sm:text-base text-[#1a3a5c] line-clamp-2 leading-snug">
          <a href={href} {...extProps} className="hover:text-amber-700 transition-colors">{e.title}</a>
        </h3>
        <p className="mt-1.5 text-xs sm:text-sm font-semibold text-neutral-700">
          {formatData(e.data)}
          {e.oraInizio && <> &middot; ore {e.oraInizio.slice(0, 5)}</>}
        </p>
        {e.location && <p className="text-xs sm:text-sm text-neutral-500 line-clamp-2">{e.location}</p>}
      </div>
    </article>
  );
}

export function EventiEsterniBlock({
  title,
  maxItems = 3,
  showArchiveLink = true,
  archiveLinkUrl = "/eventi?filter=segnalati",
  archiveLinkText = "Vedi altri →",
  className,
}: EventiEsterniBlockProps) {
  const [items, setItems] = useState<EventoEsternoDto[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    api.eventiEsterni
      .list({ size: Math.max(1, maxItems), filter: "upcoming" })
      .then((res) => { if (!cancelled) setItems(res.items); })
      .catch(() => { if (!cancelled) setItems([]); });
    return () => { cancelled = true; };
  }, [maxItems]);

  // Niente skeleton invadente: se non c'è nulla la sezione sparisce.
  if (items === null || items.length === 0) return null;

  const shown = items.slice(0, maxItems);

  return (
    <section className={`bg-neutral-50/80 ${className || ""}`}>
      <div className="px-4 sm:px-8 py-16 max-w-7xl mx-auto">
        <ScrollReveal>
          <div className="flex items-end justify-between mb-10 gap-4 flex-wrap">
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-[#1a3a5c] uppercase tracking-wide">{title}</h2>
              <div className="mt-3 h-[3px] w-16 bg-amber-600 rounded-full" />
            </div>
            {showArchiveLink && archiveLinkUrl && (
              <a href={archiveLinkUrl} className="text-sm font-semibold text-amber-700 hover:text-[#1a3a5c] transition-colors uppercase tracking-wide">
                {/* Il vecchio default salvato "Tutti i segnalati →" viene
                    rimpiazzato da "Vedi altri →" senza dover editare il blocco. */}
                {!archiveLinkText || archiveLinkText.trim() === "Tutti i segnalati →"
                  ? "Vedi altri →"
                  : archiveLinkText}
              </a>
            )}
          </div>
        </ScrollReveal>
        {shown.length === 1 ? (
          <ScrollReveal>
            <EsternoCard e={shown[0]} featured />
          </ScrollReveal>
        ) : (
          <div
            className={`grid gap-4 sm:gap-6 ${
              shown.length === 2 ? "grid-cols-1 sm:grid-cols-2" : "grid-cols-1 lg:grid-cols-3"
            }`}
          >
            {shown.map((e, i) => (
              <ScrollReveal key={e.id} delay={i * 80}>
                <EsternoCard e={e} />
              </ScrollReveal>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
